import {
    defaultPolicies,
    type Policies,
    type Room,
    type SetupSetting,
} from "./api";
// JSON value equality matches the host, including object key independence.
function equal(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (Array.isArray(a) || Array.isArray(b))
        return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => equal(v, b[i]));
    if (a && b && typeof a === "object" && typeof b === "object") {
        const left = a as Record<string, unknown>, right = b as Record<string, unknown>;
        return Object.keys(left).length === Object.keys(right).length && Object.keys(left).every(k => Object.hasOwn(right, k) && equal(left[k], right[k]));
    }
    return false;
}
function activity(settings: SetupSetting[], values: Record<string, unknown>, playerCount?: number) {
    const byKey = new Map(settings.map(s => [s.key, s]));
    const visiting = new Set<string>();
    const memo = new Map<string, boolean>();
    const groupsFor = (s: SetupSetting) => s.requiresAny ?? (s.requiresKey ? [[{key:s.requiresKey,value:s.requiresValue}]] : undefined);
    const met = (groups?: import("./api").SetupRequirement[][] | null): boolean => !groups || groups.some(group => group.every(r => {
        const target = byKey.get(r.key);
        if (!target || !active(target)) return false;
        const value = values[target.key] ?? target.default;
        return equal(value, r.value) && (target.options ?? []).filter(o => target.type === "enum" ? (o.value ?? o.id) === value : Array.isArray(value) && value.includes(o.value ?? o.id)).every(o => met(o.requiresAny));
    }));
    const active = (setting: SetupSetting): boolean => {
        const known = memo.get(setting.key);
        if (known !== undefined) return known;
        if (visiting.has(setting.key)) return false;
        visiting.add(setting.key);
        const result = (!setting.applicablePlayerCounts || playerCount !== undefined && setting.applicablePlayerCounts.includes(playerCount)) && met(groupsFor(setting));
        visiting.delete(setting.key); memo.set(setting.key, result); return result;
    };
    return {active, met};
}
export function requirementsMet(settings: SetupSetting[], values: Record<string, unknown>, groups?: import("./api").SetupRequirement[][] | null, playerCount?: number): boolean {
    return activity(settings, values, playerCount).met(groups);
}
export function enabledSettings(settings: SetupSetting[], values: Record<string, unknown>, playerCount?: number) {
    return settings.filter(activity(settings, values, playerCount).active);
}
export function availableOptions(s: SetupSetting, settings: SetupSetting[], values: Record<string, unknown>, playerCount?: number) {
    const context = activity(settings, values, playerCount);
    return (s.options ?? []).filter(o => context.met(o.requiresAny));
}
/** Choose an exact set before game start; the chosen IDs are retained in the game's rules. */
export function randomSetupSelection(options: { value?: string; id?: string }[], count: number, draw = (max: number): number => {
    const limit = Math.floor(0x100000000 / max) * max;
    const word = new Uint32Array(1);
    do { crypto.getRandomValues(word); } while (word[0]! >= limit);
    return word[0]! % max;
}): string[] {
    if (!Number.isInteger(count) || count < 1 || count > options.length) throw new Error('Invalid random setup count.');
    const pool = options.map(o => o.value ?? o.id).filter((id): id is string => !!id);
    if (pool.length !== options.length || new Set(pool).size !== pool.length) throw new Error('Invalid random setup options.');
    for (let i = 0; i < count; i++) {
        const offset = draw(pool.length - i);
        if (!Number.isInteger(offset) || offset < 0 || offset >= pool.length - i) throw new Error('Invalid random setup draw.');
        [pool[i], pool[i + offset]] = [pool[i + offset]!, pool[i]!];
    }
    return pool.slice(0, count);
}
export function validSettingValue(s: SetupSetting, v: unknown, settings?: SetupSetting[], values: Record<string, unknown> = {}, playerCount?: number): boolean {
    if (s.type === "boolean") return typeof v === "boolean";
    if (s.type === "integer") return typeof v === "number" && Number.isInteger(v) && v >= (s.minimum ?? -Infinity) && v <= (s.maximum ?? Infinity);
    if (s.type === "text") return typeof v === "string" && v.length <= (s.maximum ?? 200);
    const options = settings ? availableOptions(s, settings, values, playerCount) : s.options ?? [];
    if (s.type === "enum") return options.some(o => (o.value ?? o.id) === v);
    return Array.isArray(v) && v.length >= (s.minItems ?? 0) && v.length <= (s.maxItems ?? 256) && new Set(v).size === v.length && v.every(x => options.some(o => (o.value ?? o.id) === x));
}
function snapshot(settings: SetupSetting[], values: Record<string, unknown>) {
    return structuredClone(Object.assign({}, ...settings.filter(s => s.type === "enum").map(s => s.options?.find(o => (o.value ?? o.id) === (values[s.key] ?? s.default))?.setupValues ?? {}))) as Record<string, unknown>;
}
export function normalizeSettingValues(settings: SetupSetting[], source: Record<string, unknown>, playerCount?: number) {
    const values = { ...source }; let reset = false;
    // Dependency cycles are rejected by the host; this bounded pass settles cascaded resets.
    for (let pass = 0; pass <= settings.length; pass++) {
        let changed = false;
        for (const s of settings) {
            const options = availableOptions(s, settings, values, playerCount), value = values[s.key] ?? s.default;
            if (s.type === "enum" && !options.some(o => (o.value ?? o.id) === value)) {
                const replacement = options.find(o => (o.value ?? o.id) === s.default) ?? options[0];
                if (replacement) {
                    Object.assign(values, structuredClone(replacement.setupValues ?? {}));
                    values[s.key] = replacement.value ?? replacement.id;
                    reset = changed = true;
                }
            } else if ((s.type === "multiselect" || s.type === "catalog") && Array.isArray(value)) {
                const filtered = [...new Set(value.filter(x => options.some(o => (o.value ?? o.id) === x)))];
                if (!equal(value, filtered)) { values[s.key] = filtered; reset = changed = true; }
            }
        }
        if (!changed) break;
    }
    return { values, reset };
}
export function changeSettingValue(settings: SetupSetting[], previous: Record<string, unknown>, key: string, value: unknown, playerCount?: number) {
    // Materialize the current recipe before leaving it, then seed the next recipe.
    const values = { ...previous, ...snapshot(settings, previous), [key]: value };
    const setting = settings.find(s => s.key === key);
    const option = setting?.type === "enum" ? setting.options?.find(o => (o.value ?? o.id) === value) : undefined;
    Object.assign(values, structuredClone(option?.setupValues ?? {}));
    values[key] = value;
    return normalizeSettingValues(settings, values, playerCount);
}
export function submittedSettingValues(settings: SetupSetting[], values: Record<string, unknown>, playerCount?: number) {
    return Object.fromEntries(enabledSettings(settings, values, playerCount).map(s => [s.key, values[s.key] ?? s.default]));
}
export function validSetupValues(settings: SetupSetting[], values: Record<string, unknown>, playerCount?: number) {
    return enabledSettings(settings, values, playerCount).every(s => validSettingValue(s, values[s.key] ?? s.default, settings, values, playerCount));
}
export function restoreSettingValues(settings: SetupSetting[], remembered: Record<string, unknown>, preset: Record<string, unknown> = {}, playerCount?: number) {
    const retained = Object.fromEntries(settings.filter(s => validSettingValue(s, remembered[s.key])).map(s => [s.key, remembered[s.key]]));
    const source = { ...preset, ...retained };
    const normalized = normalizeSettingValues(settings, { ...source, ...snapshot(settings, source) }, playerCount);
    // Keep inactive recipe fields only in the local draft when snapshots are declared.
    const hasSnapshots = settings.some(s => s.options?.some(o => Object.keys(o.setupValues ?? {}).length));
    return { values: hasSnapshots ? normalized.values : submittedSettingValues(settings, normalized.values, playerCount), fallback: normalized.reset || Object.keys(retained).length !== Object.keys(remembered).length };
}
export function effectivePolicies(
    room: Pick<Room, "defaults" | "constraints">,
    overrides: Policies = room.defaults ?? defaultPolicies,
): Policies {
    const result = { ...defaultPolicies, ...overrides };
    for (const [key, rule] of Object.entries(room.constraints ?? {})) {
        if (key === "undoDepth") {
            let depth = result.undoDepth ?? null;
            if (
                rule.maximum !== undefined &&
                (depth === null || depth > rule.maximum)
            )
                depth = rule.maximum;
            if (
                rule.minimum !== undefined &&
                depth !== null &&
                depth < rule.minimum
            )
                depth = rule.minimum;
            result.undoDepth = depth;
        } else if (rule.value !== undefined)
            (result as unknown as Record<string, boolean>)[key] = rule.value;
    }
    return result;
}
